import React, {
  memo,
  useEffect,
  useState,
  forwardRef,
  useRef,
  useMemo,
} from 'react';
import { Button, Popover, Input, Switch } from 'antd';
import Draft, {
  Editor,
  EditorState,
  RichUtils,
  convertFromRaw,
  convertToRaw,
  Modifier,
  // KeyBindingUtil,
  getDefaultKeyBinding,
  convertFromHTML,
  ContentState,
} from 'draft-js';

import decorator, { PopoverLinkWrapper } from './decorator';
import Context from './context';
import {
  RICH_TYPE,
  colorStyleMap,
  colorStyleMapLightMode,
  compatibleStyleForLightMode,
  compatibleStyleForDarkMode,
  // customStyleMap,
  fontSizeOptions,
  blockStyleFn,
  fontSizeStyleMap,
} from './configs';
// import '@style/components/richEidtor.scss';
import '../../styles/components/richEidtor.scss';
// import '@style/components/DraftStyleDefault.scss';
import '../../styles/components/DraftStyleDefault.scss';
import FontSizeDropdown from './fontSizeDropdown';
import TextColorPicker from './textColorPicker';
import useTheme from '@hooks/useTheme';
// const { hasCommandModifier } = KeyBindingUtil;

// fix draftjs bug for losing focus when click icon in toolbar, see https://github.com/facebookarchive/draft-js/issues/726
const preventMouseDown = (e) => {
  e.preventDefault();
};

const EditorToolBar = ({
  addUnOrderedList,
  setLinkInput,
  linkInputConfig,
  handleInsertLink,
  showLinkInsertModal,
  noListStyle,
  setNoListStyle,
  handleCancle,
  addOrderedList,
  toggleBoldStyle,
  toggleTextColorStyle,
  toggleUnderlineStyle,
  toggleItalicStyle,
  richTypes,
  linkPopoverProps = {},
  setFontSize,
  isLightMode,
  // currentInlineStyle,
}) => {
  // const currentFontSize =
  //   fontSizeOptions.find((i) => currentInlineStyle.has(i.value))?.value ||
  //   fontSizeOptions[0]?.value;
  const BTNS = {
    [RICH_TYPE.LINK]: (
      <Popover
        key={RICH_TYPE.LINK}
        placement="bottom"
        visible={linkInputConfig.visible}
        overlayClassName="lifecycle-link-popover"
        getPopupContainer={() =>
          // document.getElementsByClassName('lifecycle-phases-container')[0] ||
          document.body
        }
        {...linkPopoverProps}
        content={
          <PopoverLinkWrapper>
            <Input
              placeholder="Insert link"
              value={linkInputConfig.url}
              onChange={(e) => {
                setLinkInput({ ...linkInputConfig, url: e.target.value });
              }}
            />
            <Button type="primary" size="small" onClick={handleInsertLink}>
              Apply
            </Button>
            <Button type="primary" size="small" onClick={handleCancle}>
              Cancel
            </Button>
          </PopoverLinkWrapper>
        }
      >
        <div className="tool-btn" onClick={showLinkInsertModal}>
          <span className="appkiticon icon-link-fill" />
        </div>
      </Popover>
    ),
    [RICH_TYPE.UNORDERED_LIST_SWITCHER]: (
      <div
        key={RICH_TYPE.UNORDERED_LIST_SWITCHER}
        className="tool-btn"
        onClick={addUnOrderedList}
      >
        <div className="list-btn">
          <span className="appkiticon icon-list-view-fill" />
          <Switch
            size="small"
            checked={!noListStyle}
            onChange={(e) => setNoListStyle(!e)}
          />
        </div>
      </div>
    ),
    [RICH_TYPE.UNORDERED_LIST]: (
      <div
        key={RICH_TYPE.UNORDERED_LIST}
        className="tool-btn"
        onClick={addUnOrderedList}
      >
        <div className="list-btn">
          <span className="appkiticon icon-list-view-fill" />
        </div>
      </div>
    ),
    [RICH_TYPE.ORDERED_LIST]: (
      <div
        key={RICH_TYPE.ORDERED_LIST}
        className="tool-btn"
        onClick={addOrderedList}
      >
        <div className="list-btn">
          <span className="appkiticon icon-richeditor-ol " />
        </div>
      </div>
    ),
    [RICH_TYPE.BOLD]: (
      <div
        key={RICH_TYPE.BOLD}
        className="tool-btn"
        onClick={toggleBoldStyle}
        onMouseDown={preventMouseDown}
      >
        <div className="list-btn">
          <span className="appkiticon icon-richeditor-bold" />
        </div>
      </div>
    ),
    [RICH_TYPE.UNDERLINE]: (
      <div
        key={RICH_TYPE.UNDERLINE}
        className="tool-btn"
        onClick={toggleUnderlineStyle}
        onMouseDown={preventMouseDown}
      >
        <div className="list-btn">
          <span className="appkiticon icon-richeditor-underline" />
        </div>
      </div>
    ),
    [RICH_TYPE.ITALIC]: (
      <div
        key={RICH_TYPE.ITALIC}
        className="tool-btn"
        onClick={toggleItalicStyle}
        onMouseDown={preventMouseDown}
      >
        <div className="list-btn">
          <span className="appkiticon icon-richeditor-italic" />
        </div>
      </div>
    ),
    [RICH_TYPE.TEXT_COLOR]: (
      <TextColorPicker
        toggleTextColorStyle={toggleTextColorStyle}
        colorStyleMap={isLightMode ? colorStyleMapLightMode : colorStyleMap}
      />
    ),
    [RICH_TYPE.FONT_SIZE]: <FontSizeDropdown onSelect={setFontSize} />,
  };
  return <div className="toolbar">{richTypes.map((type) => BTNS[type])}</div>;
};

function RichEditor(
  {
    contentSource,
    updateDataCollection,
    onChange,
    formChange = () => {},
    readOnly,
    htmlContent,
    config: { noListStyle: _noListStyle } = {},
    richTypes = [],
    maxLength,
    onInitialContentChange,
    linkPopoverProps,
    keyBindingFn: keyBindingFnFromProps,
  },
  ref,
) {
  const [myState, setMyState] = useState(EditorState.createEmpty(decorator));
  const [readonly, toggleReadOnly] = useState(false);
  const [linkInputConfig, setLinkInput] = useState({ visible: false, url: '' });
  const [noListStyle, toggleNoListStyle] = useState(_noListStyle);
  const { theme } = useTheme();
  const isLightMode = theme === 'light';

  // the function below is merged into handleReturn
  // const keyBindingFn = (e) => {
  //   if (e.keyCode === 13) {
  //     const anchorKey = myState.getSelection().getAnchorKey();
  //     const currentBlock = myState
  //       .getCurrentContent()
  //       .getBlockForKey(anchorKey);
  //     const currentBlockDepth = currentBlock.getDepth();
  //     const currentBlockType = currentBlock.getType();
  //     const currentBlockLength = currentBlock.getLength();
  //     const isList =
  //       currentBlockType === 'unordered-list-item' ||
  //       currentBlockType === 'ordered-list-item';
  //     // depth larger than 4 won't have more indent, it's a draftjs problem
  //     if (isList && currentBlockDepth >= 1 && currentBlockLength === 0) {
  //       e.shiftKey = true;
  //       const newEditorState = RichUtils.onTab(e, myState, 4);
  //       if (newEditorState !== myState) {
  //         setMyState(newEditorState);
  //       }
  //       return false;
  //     }
  //   }
  //
  //   return Draft.getDefaultKeyBinding(e);
  // };

  const handleReturn = (e, editorState) => {
    const selection = editorState.getSelection();
    const currentStyle = editorState.getCurrentInlineStyle();
    if (selection.isCollapsed() && currentStyle.size) {
      const contentState = editorState.getCurrentContent();
      const startKey = selection.getStartKey();
      const currentBlock = contentState.getBlockForKey(startKey);
      if (currentBlock) {
        const currentBlockDepth = currentBlock.getDepth();
        const currentBlockType = currentBlock.getType();
        const currentBlockLength = currentBlock.getLength();
        const isList =
          currentBlockType === 'unordered-list-item' ||
          currentBlockType === 'ordered-list-item';

        let newContentState;
        if (isList && currentBlockDepth >= 1 && currentBlockLength === 0) {
          // simulate indent decreasing
          e.shiftKey = true;
          // depth larger than 4 won't have more indent, it's a draftjs problem
          editorState = RichUtils.onTab(e, editorState, 4);
          newContentState = editorState.getCurrentContent();
        } else {
          // handle normal split block when pressing enter
          newContentState = Modifier.splitBlock(contentState, selection);
        }
        const splitState = EditorState.push(
          editorState,
          newContentState,
          'split-block',
        );

        const newSelection = splitState.getCurrentContent().getSelectionAfter();

        // store style of previous line into block meta-data
        newContentState = Modifier.mergeBlockData(
          splitState.getCurrentContent(),
          newSelection,
          {
            emptyStyle: currentStyle.toArray(),
          },
        );
        setMyState(
          EditorState.push(splitState, newContentState, 'change-block-data'),
        );
        return 'handled';
      }
    }
    return 'not-handled';
  };

  // fix draftjs bug for losing focus when click icon in toolbar, see https://github.com/facebookarchive/draft-js/issues/726
  const selectionStateBeforeBlur = useRef(undefined);

  const onEditorBlur = (e) => {
    selectionStateBeforeBlur.current = myState.getSelection();
  };

  // const editorRef = useRef();
  //unordered list
  const addUnOrderedList = () => {
    const newState = RichUtils.toggleBlockType(
      myState,
      RICH_TYPE.UNORDERED_LIST,
    );
    setMyState(newState);
  };

  //ordered list
  const addOrderedList = () => {
    const newState = RichUtils.toggleBlockType(myState, RICH_TYPE.ORDERED_LIST);

    setMyState(newState);
  };

  //bold
  const toggleBoldStyle = () => {
    const newState = RichUtils.toggleInlineStyle(myState, RICH_TYPE.BOLD);

    setMyState(newState);
  };

  // text color
  const toggleTextColorStyle = (color) => {
    const toggledColor = color.hex;
    const selection = myState.getSelection();

    // Let's just allow one color at a time. Turn off all active colors.
    const nextContentState = Object.keys(
      isLightMode ? colorStyleMapLightMode : colorStyleMap,
    ).reduce(
      (contentState, color) =>
        Modifier.removeInlineStyle(contentState, selection, color),
      myState.getCurrentContent(),
    );

    let nextEditorState = EditorState.push(
      myState,
      nextContentState,
      'change-inline-style',
    );

    const currentStyle = myState.getCurrentInlineStyle();

    // Unset style override for current color.
    if (selection.isCollapsed()) {
      nextEditorState = currentStyle.reduce(
        (state, color) => RichUtils.toggleInlineStyle(state, color),
        nextEditorState,
      );
    }

    // If the color is being toggled on, apply it.
    if (!currentStyle.has(toggledColor)) {
      nextEditorState = RichUtils.toggleInlineStyle(
        nextEditorState,
        toggledColor,
      );
    }

    setMyState(nextEditorState);
  };

  // font size
  const setFontSize = (size) => {
    const selection =
      selectionStateBeforeBlur.current || myState.getSelection();
    // 没有选中元素的情况-暂时废弃，因为必须加内容才能有块，否则插入不进去一个空块。。不满足需求
    // spike需求
    // if (selection.isCollapsed()) {
    //   // 插入一个空标签并设置字体大小样式
    //   const contentState = myState.getCurrentContent();
    //   const selectionState = myState.getSelection();
    //   const sampleMarkup = `<span class="test" style="font-size: ${size.split('-')[1]}px;">{{{我是插入进来的！！！}}}</span>`;
    //   const blocksFromHTML = convertFromHTML(sampleMarkup);
    //   const blockMap = contentState.getBlocksAsArray();
    //   blocksFromHTML.contentBlocks = blockMap.concat(blocksFromHTML.contentBlocks);
    //   console.log('blockMap: ', blockMap);
    //   const newContentState =
    //         ContentState.createFromBlockArray(blocksFromHTML, blocksFromHTML.entityMap);
    //     const newEditorState = EditorState.createWithContent(newContentState);
    //   return setMyState(EditorState.forceSelection(newEditorState, selection));
    // }
    // 前一个单词和后一个单词设置字体大小 - 失败。。。
    // if (selection.isCollapsed()) {
    //   const blockBefore = myState.getCurrentContent().getBlockBefore(selection.getStartKey());
    //   const blockAfter = myState.getCurrentContent().getBlockAfter(selection.getStartKey());
    //   console.log('blockBefore: ', blockBefore);
    //   console.log('blockAfter: ', blockAfter);
    //   if (blockBefore) {
    //     blockBefore.style = size;
    //   }
    //   if (blockAfter) {
    //     blockAfter.style = size;
    //   }
    //   const nextContentState = myState.getCurrentContent();
    //   return setMyState(EditorState.forceSelection(nextContentState, selection));
    // }

    // 没有选中元素的情况-暂时废弃，因为必须加内容才能有块，否则插入不进去一个空块。。不满足需求
    // spike需求
    // if (selection.isCollapsed()) {
    //   // 插入一个空标签并设置字体大小样式
    //   const contentState = myState.getCurrentContent();

    //   const selectionState = myState.getSelection();

    //   const sampleMarkup = `<span class="test" style="font-size: ${size.split('-')[1]}px;">{{{我是插入进来的！！！}}}</span>`;

    //   const blocksFromHTML = convertFromHTML(sampleMarkup);

    //   const blockMap = contentState.getBlocksAsArray();

    //   blocksFromHTML.contentBlocks = blockMap.concat(blocksFromHTML.contentBlocks);

    //   console.log('blockMap: ', blockMap);
    //   const newContentState =
    //         ContentState.createFromBlockArray(blocksFromHTML, blocksFromHTML.entityMap);
    //     const newEditorState = EditorState.createWithContent(newContentState);

    //   return setMyState(EditorState.forceSelection(newEditorState, selection));
    // }

    // 前一个单词和后一个单词设置字体大小 - 失败。。。
    // if (selection.isCollapsed()) {
    //   const blockBefore = myState.getCurrentContent().getBlockBefore(selection.getStartKey());
    //   const blockAfter = myState.getCurrentContent().getBlockAfter(selection.getStartKey());
    //   console.log('blockBefore: ', blockBefore);
    //   console.log('blockAfter: ', blockAfter);
    //   if (blockBefore) {
    //     blockBefore.style = size;
    //   }
    //   if (blockAfter) {
    //     blockAfter.style = size;
    //   }
    //   const nextContentState = myState.getCurrentContent();
    //   return setMyState(EditorState.forceSelection(nextContentState, selection));
    // }
    const nextContentState = fontSizeOptions
      .map((i) => i.value)
      .reduce(
        (contentState, sizeStyleName) =>
          Modifier.removeInlineStyle(contentState, selection, sizeStyleName),
        myState.getCurrentContent(),
      );

    let nextEditorState = EditorState.push(
      myState,
      nextContentState,
      'change-inline-style',
    );

    const currentStyle = myState.getCurrentInlineStyle();

    if (!currentStyle.has(size)) {
      nextEditorState = RichUtils.toggleInlineStyle(nextEditorState, size);
    }
    setMyState(EditorState.forceSelection(nextEditorState, selection));
  };

  //underline
  const toggleUnderlineStyle = () => {
    const newState = RichUtils.toggleInlineStyle(myState, RICH_TYPE.UNDERLINE);

    setMyState(newState);
  };

  // italic
  const toggleItalicStyle = () => {
    const newState = RichUtils.toggleInlineStyle(myState, RICH_TYPE.ITALIC);

    setMyState(newState);
  };

  //list
  const showLinkInsertModal = () => {
    const currentSelection = myState.getSelection();
    if (!currentSelection.isCollapsed()) {
      const contentState = myState.getCurrentContent();
      const startKey = currentSelection.getStartKey();
      const startOffset = currentSelection.getStartOffset();
      const endOffset = currentSelection.getEndOffset();
      const blockWithLinkAtBeginning = contentState.getBlockForKey(startKey);
      const linkKey = blockWithLinkAtBeginning.getEntityAt(startOffset);
      const selectedText = blockWithLinkAtBeginning
        .getText()
        .slice(startOffset, endOffset);

      let url = '';
      if (linkKey) {
        const linkInstance = contentState.getEntity(linkKey);
        url = linkInstance.getData().url;
      }

      setLinkInput({
        visible: true,
        url,
        selectedText,
      });
    }
  };

  const handleClearLinkInput = () => {
    setLinkInput({
      url: '',
      visible: false,
      selectedText: '',
    });
  };
  const handleEditLink = (entityKey, newlink) => {
    const contentState = myState.getCurrentContent();
    contentState.mergeEntityData(entityKey, {
      url: newlink,
    });
    const newEditorState = EditorState.set(myState, {
      currentContent: contentState,
    });
    setMyState(newEditorState);
  };

  const handleInsertLink = (isEditing = false) => {
    if (!myState.getSelection().isCollapsed() && linkInputConfig.url) {
      const contentState = myState.getCurrentContent();
      const contentStateWithEntity = contentState.createEntity(
        RICH_TYPE.LINK,
        'MUTABLE',
        {
          url: linkInputConfig.url,
          stateSetter: setMyState,
        },
      );

      const entityKey = contentStateWithEntity.getLastCreatedEntityKey();

      const newEditorState = EditorState.set(myState, {
        currentContent: contentStateWithEntity,
      });
      setMyState(
        EditorState.moveSelectionToEnd(
          RichUtils.toggleLink(
            newEditorState,
            newEditorState.getSelection(),
            entityKey,
          ),
        ),
      );
    }

    handleClearLinkInput();
  };

  // const myKeyBindingFn = (e) => {
  //   if (e.keyCode === 9 /* `Tab` key */) {
  //     return "myeditor-tab";
  //   }
  //   return getDefaultKeyBinding(e);
  // };
  //
  // const handleKeyCommand = (command) => {
  //   if (command === "myeditor-tab") {
  //     console.log('hfdiashfidsai')
  //     // const newEditorState = RichUtils.onTab(e, myState, 6 /* maxDepth */)
  //     // if (newEditorState !== myState) {
  //     //   setMyState(newEditorState)
  //     // }
  //     return "handled";
  //   }
  //   return "not-handled";
  // };
  const handleKeyBindings = (e) => {
    if (e.keyCode === 9) {
      const newEditorState = RichUtils.onTab(e, myState, 6 /* maxDepth */);
      if (newEditorState !== myState) {
        setMyState(newEditorState);
      }

      return;
    }

    return getDefaultKeyBinding(e);
  };

  useEffect(() => {
    // EditorState.moveFocusToEnd(EditorState.createWithContent(
    //   convertFromRaw(contentSource),
    //   decorator,
    // ))
    setMyState(
      contentSource
        ? EditorState.createWithContent(
            convertFromRaw(contentSource),
            decorator,
          )
        : EditorState.createEmpty(decorator),
    );
    if (readOnly) {
      toggleNoListStyle(_noListStyle);

      handleClearLinkInput();
    }
  }, [readOnly, onInitialContentChange]);

  useEffect(() => {
    const contentState = myState.getCurrentContent();
    updateDataCollection &&
      updateDataCollection(
        contentState,
        noListStyle,
        htmlContent,
        _noListStyle,
      ); // updateDataCollection in lifeCycle

    onChange && onChange(convertToRaw(contentState)); //update value in form
  }, [myState, noListStyle]);

  const handleBeforeInput = (e, editorState) => {
    if (maxLength) {
      const contentState = editorState.getCurrentContent();
      const currentLength = contentState.getPlainText().length;
      return currentLength >= maxLength ? 'handled' : 'not-handled';
    }
  };

  const handlePastedText = (text, html, editorState) => {
    if (maxLength) {
      const contentState = editorState.getCurrentContent();
      const currentLength = contentState.getPlainText().length;
      return currentLength + text.length >= maxLength;
    }

    return false;
  };

  // const currentInlineStyle = myState.getCurrentInlineStyle();

  const customStyleMap = useMemo(
    () => ({
      ...(isLightMode ? colorStyleMapLightMode : colorStyleMap),
      ...(isLightMode
        ? compatibleStyleForLightMode
        : compatibleStyleForDarkMode),
      ...fontSizeStyleMap,
    }),
    [isLightMode],
  );

  return (
    <div
      className={`dd-rich-editor ${noListStyle ? 'no-list-style' : ''} ${
        readOnly ? '' : 'is-editing'
      }
     `}
    >
      <EditorToolBar
        addUnOrderedList={addUnOrderedList}
        addOrderedList={addOrderedList}
        toggleBoldStyle={toggleBoldStyle}
        toggleTextColorStyle={toggleTextColorStyle}
        toggleUnderlineStyle={toggleUnderlineStyle}
        toggleItalicStyle={toggleItalicStyle}
        setLinkInput={setLinkInput}
        handleInsertLink={handleInsertLink}
        showLinkInsertModal={showLinkInsertModal}
        setNoListStyle={toggleNoListStyle}
        linkInputConfig={linkInputConfig}
        noListStyle={noListStyle}
        handleCancle={handleClearLinkInput}
        richTypes={richTypes}
        linkPopoverProps={linkPopoverProps}
        setFontSize={setFontSize}
        isLightMode={isLightMode}
        // currentInlineStyle={currentInlineStyle}
      />
      <Context.Provider
        value={{
          stateSetter: setMyState,
          readonlySetter: toggleReadOnly,
          handleEditLink,
          displayMode: readOnly,
          linkPopoverProps,
        }}
      >
        <Editor
          ref={ref}
          readOnly={readOnly || readonly}
          editorState={myState}
          customStyleMap={customStyleMap}
          onChange={(...args) => {
            setMyState(...args);
            formChange();
          }}
          // handleKeyCommand={handleKeyCommand}
          handlePastedText={handlePastedText}
          handleBeforeInput={handleBeforeInput}
          keyBindingFn={keyBindingFnFromProps}
          // handleKeyCommand={handleKeyCommand}
          onTab={handleKeyBindings}
          // handleKeyCommand={handleKeyCommand}
          onBlur={onEditorBlur}
          // blockRendererFn={blockRendererFn}
          blockStyleFn={blockStyleFn}
          handleReturn={handleReturn}
          customStyleFn={(styleOrderList, contentBlock) => {
            const emptyStyleList = contentBlock?.getData()?.get('emptyStyle');
            if (
              styleOrderList.size === 0 &&
              emptyStyleList?.size &&
              contentBlock.getLength() === 0 &&
              emptyStyleList?.length
            ) {
              const defaultFontSize = emptyStyleList
                .toArray()
                .find((i) => i && i.startsWith('fontSize'));
              if (defaultFontSize) {
                const fontSize = defaultFontSize.split('-')[1];
                return {
                  fontSize: `${fontSize}px`,
                };
              }
            }
            return {};
          }}
        />
      </Context.Provider>
    </div>
  );
}

RichEditor.propTypes = {};

export default memo(forwardRef(RichEditor));
